import React, { useState, useEffect, useRef } from "react"
import { KeyboardArrowLeft, KeyboardArrowRight } from "@material-ui/icons"
import config from "../../config"

const BannerCarousel = ({
  banners = [], // Array de banners
  fallbackImage, // No se usará en este caso, se mantiene por si se requiere en el futuro
  onBannerClick, // Función para manejar clics en los banners
  height = 240, // Altura del carrusel por defecto
  section, // Objeto de sección, se espera que contenga { _id | id, name, portrait, seccion }
}) => {
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0)
  const timerRef = useRef(null)

  // Estado para calcular dinámicamente la altura del contenedor del fallback
  const [fallbackHeight, setFallbackHeight] = useState(height)

  useEffect(() => {
    // Si no hay banners y existe la sección, se calcula la altura
    if (banners.length === 0 && section) {
      const updateFallbackHeight = () => {
        // Se selecciona el objeto correcto: section.portrait o section.seccion.portrait
        let portrait =
          section?.portrait && section.portrait.url
            ? section.portrait
            : section?.seccion && section.seccion.portrait
            ? section.seccion.portrait
            : null

        // Si contamos con las dimensiones, se calcula el aspect ratio
        if (portrait && portrait.width && portrait.height) {
          const aspectRatio = portrait.height / portrait.width
          setFallbackHeight(window.innerWidth * aspectRatio)
        } else {
          setFallbackHeight(height)
        }
      }

      updateFallbackHeight()
      window.addEventListener("resize", updateFallbackHeight)
      return () => window.removeEventListener("resize", updateFallbackHeight)
    }
  }, [banners.length, section, height])

  // Función para cambiar al siguiente banner
  const nextBanner = () => {
    setCurrentBannerIndex(prevIndex => (prevIndex + 1) % banners.length)
  }

  // Función para cambiar al banner anterior
  const prevBanner = () => {
    setCurrentBannerIndex(
      prevIndex => (prevIndex - 1 + banners.length) % banners.length
    )
  }

  // Función para reiniciar el temporizador
  const resetTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current)
    timerRef.current = setInterval(() => nextBanner(), 5000)
  }

  // Manejar cambio manual
  const handleManualBannerChange = newIndex => {
    setCurrentBannerIndex(newIndex)
    resetTimer()
  }

  // Iniciar y limpiar temporizador
  useEffect(() => {
    resetTimer()
    return () => clearInterval(timerRef.current)
  }, [banners.length])

  // Cuando no hay banners, se evaluará la sección
  if (banners.length === 0) {
    // Si la sección es la de id '65301f6e965e9b12688fd047', mostramos el recuadro gris con "Cargando..."
    if (
      section &&
      (section._id === "65301f6e965e9b12688fd047" || section.id === "65301f6e965e9b12688fd047")
    ) {
      return (
        <div
          className="flex items-center justify-center relative"
          style={{
            height: height,
            width: "100%",
            backgroundColor: "#0000001C", // Fondo gris
          }}
        >
          <span className="text-white text-lg">Cargando...</span>
        </div>
      )
    } else {
      // Para otras secciones, mostramos la imagen de fallback usando el fallbackHeight calculado
      return (
        <>
          {section && section.portrait && section.portrait.url ? (
            <div className="flex relative" style={{ height: fallbackHeight, width: "100%" }}>
              <img fetchpriority="high" onError={(e) => { e.target.style.display = "none"; }}
                className="absolute"
                src={
                  section.portrait.url.trim().startsWith("http")
                    ? section.portrait.url.trim()
                    : config.serverUri + section.portrait.url.trim()
                }
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
                alt={section.name}
              />
            </div>
          ) : (
            <div className="flex relative" style={{ height: fallbackHeight, width: "100%" }}>
              {/* test */}
              {/* {JSON.stringify(section)} */}
              {/* <img fetchpriority="high" onError={(e) => { e.target.style.display = "none"; }}
                className="absolute"
                src={
                  section.seccion.portrait.url.trim().startsWith("http")
                    ? section.seccion.portrait.url.trim()
                    : config.serverUri + section.seccion.portrait.url.trim()
                }
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
                alt={section.name}
              /> */}
              <img fetchpriority="high" onError={(e) => { e.target.style.display = "none"; }}
                className="absolute"
                src={section.portrait}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
                alt={section.name}
              />
            </div>
          )}
        </>
      )
    }
  }

  // Si hay banners, se renderiza el carrusel completo
  return (
    <>
      {/* Flecha izquierda */}
      {currentBannerIndex > 0 && (
        <div
          className="absolute left-8 top-1/2 transform -translate-y-1/2 z-10 shadow-lg"
          onClick={() => {
            prevBanner()
            resetTimer() // Resetear el temporizador al hacer clic manualmente
          }}
          style={{
            background: "none",
            border: "none",
            padding: "15px",
            cursor: "pointer",
          }}
        >
          <KeyboardArrowLeft
            style={{
              fontSize: 90, // Flechas mucho más grandes
              color: "white",
            }}
          />
        </div>
      )}

      {/* Contenedor de las imágenes con animación */}
      <div
        className="flex transition-transform duration-700 ease-in-out relative"
        style={{
          transform: `translateX(-${currentBannerIndex * 100}%)`,
          width: `${banners.length * 100}%`, // Ancho proporcional al número de banners
          height: "auto", // El contenedor se adapta al tamaño de la imagen
        }}
      >
        {banners.map((banner, index) => (
          <div
            key={index}
            className="flex-shrink-0 relative"
            style={{
              width: "100%",
              position: "relative",
              cursor:
                banner.haveRoute && !banner.showRouteButton ? "pointer" : "default",
              height: "auto", // Ajustar altura según la imagen
            }}
            onClick={() => onBannerClick(banner)}
          >
            {/* Imagen clicable según la lógica */}
            <img
              className="w-full object-cover"
              src={
                banner?.desktopImage.url.trim().startsWith("http")
                  ? banner?.desktopImage.url.trim()
                  : config.serverUri + banner?.desktopImage.url.trim()
              }
              alt={section.name}
              loading={index === 0 ? "eager" : "lazy"}
              style={{
                position: "relative",
                zIndex: 0,
                height: "auto", // La imagen determina la altura
                marginBottom: "0px",
              }}
            />

            {/* Contenedor centrado para el título y el botón */}
            <div
              className="absolute inset-0 flex flex-col justify-center items-center sm:pb-[50px]"
              style={{ zIndex: 2 }}
            >
              {banner.haveTitle && banner.title && (
                <p
                  className="text-white text-2xl font-bold mb-4"
                  style={{
                    textShadow: "2px 2px 4px rgba(0, 0, 0, 0.5)",
                  }}
                >
                  {banner.title}
                </p>
              )}

              {banner.showRouteButton && (
                <button
                  onClick={() => onBannerClick(banner)}
                  className="bg-black text-white py-1 px-4 rounded-full shadow-lg hover:bg-gray-900"
                >
                  Ingresar ahora
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Flecha derecha */}
      {currentBannerIndex < banners.length - 1 && (
        <div
          className="absolute right-8 top-1/2 transform -translate-y-1/2 z-10 shadow-lg"
          onClick={() => {
            nextBanner()
            resetTimer() // Resetear el temporizador al hacer clic manualmente
          }}
          style={{
            background: "none",
            border: "none",
            padding: "15px",
            cursor: "pointer",
          }}
        >
          <KeyboardArrowRight
            style={{
              fontSize: 90, // Flechas mucho más grandes
              color: "white",
            }}
          />
        </div>
      )}

      {/* Indicadores de los banners (círculos) */}
      <div
        className="absolute flex justify-center w-full bottom-4"
        style={{ zIndex: 2 }}
      >
        {banners.map((_, index) => (
          <div
            key={index}
            className={`h-3 w-3 mx-2 rounded-full cursor-pointer ${
              index === currentBannerIndex
                ? "bg-sc-primary"
                : "bg-gray-300 hover:bg-gray-400"
            }`}
            onClick={() => handleManualBannerChange(index)}
          />
        ))}
      </div>
    </>
  )
}

export default BannerCarousel
